/**
 * Canonical moderation policy — auto-reject, auto-review, allowed, remove.
 * Used by the moderation engine and published in admin tooling.
 */

export const MODERATION_POLICY_VERSION = '2026-07-24.2'

export type ModerationDecision = 'reject' | 'review' | 'approve'

export type ModerationSignal =
  | 'suspected_under_18'
  | 'age_claim_mismatch'
  | 'csam_escalation'
  | 'non_consensual_intimate'
  | 'extremist_content'
  | 'scam_or_phishing'
  | 'explicit_nudity'
  | 'graphic_violence'
  | 'personal_injury'
  | 'self_harm_imagery'
  | 'heavy_drug_use'
  | 'deepfake_suspected'
  | 'duplicate_photo'
  | 'report_spike'
  | 'spam_behavior'
  | 'impersonation'
  | 'minor_solicitation'
  | 'scan_unavailable'
  | 'clean'

export const AUTO_REJECT: { signal: ModerationSignal; label: string }[] = [
  { signal: 'suspected_under_18', label: 'Suspected under 18 (AI age estimate below threshold)' },
  { signal: 'csam_escalation', label: 'Child sexual abuse material — immediate removal and escalation' },
  { signal: 'non_consensual_intimate', label: 'Non-consensual intimate images' },
  { signal: 'extremist_content', label: 'Terrorist / extremist content' },
  { signal: 'scam_or_phishing', label: 'Malware, phishing, or scam links' },
  { signal: 'spam_behavior', label: 'Spam / bot accounts' },
  { signal: 'impersonation', label: 'Impersonation of another person' },
  { signal: 'minor_solicitation', label: 'Explicit solicitation involving minors' },
]

export const AUTO_REVIEW: { signal: ModerationSignal; label: string }[] = [
  { signal: 'age_claim_mismatch', label: "Claimed age doesn't match AI estimate by a significant margin" },
  { signal: 'explicit_nudity', label: 'Explicit nudity' },
  { signal: 'graphic_violence', label: 'Graphic violence, corpses, or gore of others' },
  { signal: 'self_harm_imagery', label: 'Possible self-harm imagery — human review' },
  { signal: 'heavy_drug_use', label: 'Heavy drug use imagery' },
  { signal: 'deepfake_suspected', label: 'Deepfakes / suspected AI impersonation (high-confidence only)' },
  { signal: 'report_spike', label: 'Multiple user reports in a short period' },
  { signal: 'duplicate_photo', label: 'Duplicate image detected on another account' },
  { signal: 'scan_unavailable', label: 'AI scan unavailable — human review required' },
]

/** Signals that are informational only and do not block approval on their own. */
export const ALLOW_SIGNALS = new Set<ModerationSignal>(['personal_injury', 'clean'])

export const ALLOWED_CONTENT = [
  'Shirtless photos',
  'Swimwear',
  'Lingerie (within brand standards)',
  'Kissing and mild affectionate content',
  'Tattoos, piercings, body modifications',
  'Scars, wounds, bruises, and bloody injuries on your own body (with your face visible)',
  'LGBTQ+ content on the same basis as any other content',
  'Smoking / drinking when legal and accurately represented',
]

export const REMOVE_CATEGORIES = [
  'Minors',
  'Sexual content involving minors',
  'Revenge porn',
  'Blackmail / extortion',
  'Doxxing / personal information',
  'Fraud / scams',
  'Catfishing using another person\'s photos',
  'Threats of violence',
  'Harassment / stalking',
  'Bestiality',
  'Necrophilia',
]

/** Age thresholds (Sightengine minor probability → review band — estimates are approximate) */
export const AGE_REJECT_BELOW = 18
export const AGE_REVIEW_BELOW = 21
export const AGE_MISMATCH_GAP = 8

export const REPORT_SPIKE_COUNT = 3
export const REPORT_SPIKE_HOURS = 24
